import axios, { type AxiosResponse } from 'axios';

import type { HostGroupWithHostProfilesPartialDetails, PaginatedZoneData, Zone } from '@sine/sine-core-api-types';

import type { OffsetLimit, SortDirection } from '../../shared/customHooks/useOffsetLimitPagination';
import type { CreateZoneBody, SearchZonesParams, UpdateZoneBody } from './zones.types';

const zonesService = {
  async createZone(siteId: string, body: CreateZoneBody) {
    const res = await axios.post<Zone>(`${window.env.REACT_APP_SINE_CORE_API_URI}/zone/sites/${siteId}`, body);
    return res.data;
  },

  async updateZone(zoneId: string, body: UpdateZoneBody) {
    const res = await axios.patch<Zone>(`${window.env.REACT_APP_SINE_CORE_API_URI}/zone/${zoneId}`, body);
    return res.data;
  },

  async deleteZone(zoneId: string) {
    await axios.delete(`${window.env.REACT_APP_SINE_CORE_API_URI}/zone/${zoneId}`);
  },

  async getZonesForSite(siteId: string, params?: OffsetLimit) {
    const res = await axios.get<Array<Zone>>(`${window.env.REACT_APP_SINE_CORE_API_URI}/zone/sites/${siteId}`, {
      params,
    });
    return res.data;
  },

  async searchZonesForSite(siteId: string, searchTerm: string, offsetLimit: OffsetLimit, sortDirection: SortDirection) {
    const res = await axios.post<PaginatedZoneData, AxiosResponse<PaginatedZoneData>, SearchZonesParams>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/zone/search/sites/${siteId}`,
      {
        filters: {
          searchTerm: searchTerm.trim() || undefined, // Use undefined if searchTerm is empty
        },
      },
      {
        params: {
          limit: offsetLimit.limit,
          page: Math.round(offsetLimit.offset / offsetLimit.limit) + 1, // Convert offset to page number
          sort: sortDirection,
        },
      },
    );
    return res.data;
  },

  async getAvailableHostGroups(siteId: string) {
    const res = await axios.get<Array<HostGroupWithHostProfilesPartialDetails>>(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/zone/hostgroups/${siteId}`,
    );
    return res.data;
  },

  async getZonesForHostProfile(siteId: string, hostId: string) {
    try {
      const res = await axios.get<Array<Zone>>(
        `${window.env.REACT_APP_SINE_CORE_API_URI}/zone/sites/${siteId}/hosts/${hostId}`,
      );
      return res.data;
    } catch (err: any) {
      return Promise.reject(err.data);
    }
  },
};

export default zonesService;
